import { type SupplierListItem } from '@choco-admin/types';
import { Card } from '@heroui/react';
import React from 'react';
import { Controller } from 'react-hook-form';

import { AlertCard } from '../../../../../../components/AlertCard';
import {
  ChocoCheckbox,
  ChocoSelect,
} from '../../../../../../components/FormComponents';
import { PageLoading } from '../../../../../../components/PageLoading';
import { useSupplierForm } from '../../SupplierFormProvider';

import { useSubCatalogDropdown } from './hooks/useSubCatalogDropdown';

interface CatalogTabProps {
  supplier?: SupplierListItem;
}

export const CatalogTab: React.FC<CatalogTabProps> = ({ supplier }) => {
  const {
    formMethods,
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  const { dropDownItems, loading, error } = useSubCatalogDropdown(supplier?.id);

  const defaultSubCatalog = watch('supplierDefaultSubCatalog');

  const defaultOptions = [
    { value: '', label: 'Show all products' },
    { value: 'NO_CATALOG', label: 'Hide all products' },
  ];

  const allOptions = [
    ...defaultOptions,
    ...dropDownItems.map((item) => ({
      value: item.id,
      label: item.name,
    })),
  ];

  const selectedOption =
    allOptions.find((option) => option.value === defaultSubCatalog) ||
    defaultOptions[0];

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to configure catalog settings."
      />
    );
  }

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <AlertCard
        type="error"
        title="Error loading catalog settings"
        message={error.message}
      />
    );
  }

  return (
    <div className="space-y-6">
      <Card className="p-8 rounded-xl">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Default Catalog</h3>
            <ChocoSelect
              aria-label="Default catalog selection"
              label="Default catalog"
              selectedKeys={[defaultSubCatalog]}
              value={selectedOption.label}
              onChange={(e) =>
                setValue('supplierDefaultSubCatalog', e.target.value)
              }
              errorMessage={errors.supplierDefaultSubCatalog?.message}
              className="max-w-md"
              options={allOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
                key: opt.value,
              }))}
            />
            <p className="text-sm text-gray-500 mt-2">
              This default can be overwritten for each chat separately.
            </p>
          </div>

          <AlertCard
            type="warning"
            title="This distributor has been migrated to LOX"
            message="Please use the available Retool app for viewing and updating distributor's catalog."
          />

          <div>
            <h3 className="text-lg font-semibold mb-4">Prices</h3>
            {supplier?.isIntegrated ? (
              <div className="mb-4">
                <>
                  <AlertCard
                    type="info"
                    title="Locked as an integration setting for this distributor"
                    message="The following settings are controlled by the integration."
                  />
                  <div className="mt-2">
                    <Controller
                      name="displayToCustomers"
                      control={formMethods.control}
                      render={({ field: { value } }) => (
                        <ChocoCheckbox
                          checked={value}
                          isSelected={value}
                          isDisabled
                          label="Show prices in catalogs for all customers"
                          description="Customers, who the distributor already has a chat with on Choco, will be able to see prices."
                          color="primary"
                        />
                      )}
                    />
                  </div>
                </>
              </div>
            ) : (
              <div className="mb-4">
                <Controller
                  name="displayToCustomers"
                  control={formMethods.control}
                  defaultValue={
                    supplier?.priceSetting?.displayToCustomers ?? false
                  }
                  render={({ field: { value, onChange } }) => (
                    <ChocoCheckbox
                      checked={value}
                      isSelected={value}
                      onChange={(e) => onChange(e.target.checked)}
                      label="Show prices in catalogs for all customers"
                      description="Customers, who the distributor already has a chat with on Choco, will be able to see prices."
                      color="primary"
                    />
                  )}
                />
              </div>
            )}

            <div className="mb-4">
              <Controller
                name="displayInMarketplace"
                control={formMethods.control}
                defaultValue={supplier?.priceSetting?.displayInMarketplace}
                render={({ field: { value, onChange } }) => {
                  return (
                    <ChocoCheckbox
                      checked={value}
                      isSelected={value}
                      onChange={(e) => onChange(e.target.checked)}
                      label="Show prices to distributor users"
                      description="All distributor users will be able to see catalog/negotiated prices."
                      color="primary"
                    />
                  );
                }}
              />
            </div>

            <div>
              <Controller
                name="supplierHideMyPrice"
                control={formMethods.control}
                defaultValue={supplier?.hideMyPrice ?? false}
                render={({ field: { value, onChange } }) => (
                  <ChocoCheckbox
                    checked={value}
                    isSelected={value}
                    onChange={(e) => onChange(e.target.checked)}
                    label="Hide My Price"
                    description="Prevents users from being able to view or update My Price via the app"
                    color="primary"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
